import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, PieChart, Pie, Legend, CartesianGrid, ReferenceLine
} from 'recharts';
import {
  Users, Briefcase, CreditCard, DollarSign, Calendar,
  CheckCircle, Clock, Trash2, Edit2, Edit3, PlaySquare, AlertCircle, X,
  FileText, Search, Package, Repeat, TrendingUp, TrendingDown, Download, PieChart as PieChartIcon,
  Target, UserCheck, XCircle, Plus, ChevronDown, ChevronUp, Filter, ArrowUpRight, ArrowDownRight, Video, Wallet, HandCoins
} from 'lucide-react';
import TreasuryPage from './TreasuryPage';
import api, {
  getSubscriptions, getInvoices, getExpenses, getMonthlyReport, getAITools,
  getCurrentRate, setExchangeRate, formatIQD, formatUSD, createExpense, updateExpense, getExpenseCategories, updateInvoice,
  deleteExpense, recordPayment, createInvoice, getClients, getClient,
  createClient, getClientLTV, getOverdueInvoices, createSubscription, getAllPackages,
  generateSubInvoice, getProjects, createProject, updateProject,
  updateSubscription, deleteSubscription, cancelSubscription,
  cancelInvoice, reversePayment, getInvoice,
  updateProjectStatus, archiveProject, getProjectSummary,
  getInvoiceStatement, getDailySales, getDailyDeliveries,
  getUsers, createUser, updateUser, deleteUser,
  getDeliverables, createDeliverable, deleteDeliverable,
  deleteProject, deleteInvoice, getEmployees,
  generateTrackingToken
} from '../utils/api';
import { parseArabicToEnglish } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  EXCHANGE_RATE, S, iqd2usd, td, pill, btn, today, Dual, StatCard, FilterBtn, Modal, LabeledInput, DetailRow, EmptyRow, DateRangeFilter, VIDEO_TYPES, DeliveryModal, VT_LABEL, VT_FILTER_TABS
} from '../components/SharedUI';


// ═══════════════════════════════════════════════════════════════
// EXPENSES PAGE
// ═══════════════════════════════════════════════════════════════
const BLANK_EXP = () => ({
  category: '', subcategory: '', expense_date: new Date().toISOString().split('T')[0], amount_iqd: '', note: '', employee_id: '',
  bonus: '', deduction: '', base_salary: ''
});

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catMap, setCatMap] = useState({});
  const [catFilter, setCatFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(BLANK_EXP());
  const [employees, setEmployees] = useState([]);
  const [activeFinanceTab, setActiveFinanceTab] = useState('expenses'); // 'expenses', 'treasury', 'debts'

  const load = useCallback(() => {
    const p = {};
    if (catFilter) p.category = catFilter;
    if (fromDate) p.from_date = fromDate;
    if (toDate) p.to_date = toDate;
    getExpenses(p).then(setExpenses);
    getEmployees().then(setEmployees).catch(() => { });
    getExpenseCategories().then(cats => {
      setCategories(cats);
      const cm = {};
      cats.forEach(c => { cm[c.key] = c; });
      setCatMap(cm);
    }).catch(() => { });
  }, [catFilter, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const submit = async e => {
    e.preventDefault();
    if (!form.amount_iqd || parseFloat(form.amount_iqd) <= 0) return toast.error('أدخل مبلغاً صحيحاً');
    try {
      let finalNote = form.note;
      if (form.category === 'salary') {
        const details = [];
        const b = parseInt(form.bonus) || 0;
        const d = parseInt(form.deduction) || 0;
        if (b > 0) details.push(`مكافأة: ${formatIQD(b)}`);
        if (d > 0) details.push(`استقطاع: ${formatIQD(d)}`);
        if (details.length > 0) {
          finalNote = finalNote ? `${finalNote} | ${details.join(' - ')}` : details.join(' - ');
        }
      }

      if (editId) {
        await updateExpense(editId, { ...form, note: finalNote, amount_usd: parseFloat(form.amount_iqd) / EXCHANGE_RATE });
        toast.success('تم تحديث المصروف');
      } else {
        await createExpense({ ...form, note: finalNote, amount_usd: parseFloat(form.amount_iqd) / EXCHANGE_RATE });
        toast.success('تم إضافة المصروف');
      }
      setShowForm(false);
      setEditId(null);
      setForm(BLANK_EXP());
      load();
    } catch { toast.error('خطأ في العملية'); }
  };

  const handleDelete = async id => {
    if (!window.confirm('حذف هذا المصروف نهائياً؟')) return;
    try { await deleteExpense(id); toast.success('تم الحذف'); load(); }
    catch { toast.error('خطأ في الحذف'); }
  };

  // ── Derived ──────────────────────────────────────────────────
  const grandTotal = expenses.reduce((s, e) => s + (e.amount_iqd || 0), 0);
  const byCategory = Object.entries(catMap).map(([k, v]) => ({
    key: k, ...v,
    total: expenses.filter(e => e.category === k).reduce((s, e) => s + (e.amount_iqd || 0), 0)
  })).filter(c => c.total > 0);
  const pieData = byCategory.map(c => ({ name: c.label, value: c.total }));
  const expPreviewUSD = iqd2usd(parseFloat(form.amount_iqd || 0));

  const openCreate = () => { setForm(BLANK_EXP()); setEditId(null); setShowForm(true); };
  const openEdit = (e) => {
    setForm({
      ...BLANK_EXP(), ...e,
      amount_iqd: e.amount_iqd || '',
      bonus: '', deduction: '', base_salary: e.category === 'salary' ? (employees.find(emp => emp.id == e.employee_id)?.base_salary_iqd || 0) : ''
    });
    setEditId(e.id);
    setShowForm(true);
  };

  return (
    <div>
      {/* ── Finance Header & Tabs ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 className="dark:!text-gray-100" style={{ margin: 0, fontSize: 24, fontWeight: 800, color: 'var(--text-dark-h, #0F1E33)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <DollarSign size={24} color="#2980B9" /> الإدارة المالية
        </h1>
        {activeFinanceTab === 'expenses' && (
          <button onClick={openCreate} style={btn('#E74C3C')}>
            <Plus size={15} /> مصروف جديد
          </button>
        )}
      </div>

      <div className="flex gap-2 border-b border-[var(--border-light)] pb-4 mb-6">
        <button
          className={`px-5 py-2.5 text-sm font-bold rounded-xl transition-all ${activeFinanceTab === 'expenses' ? 'bg-[#E74C3C] text-white shadow-md' : 'text-[var(--text-muted)] hover:bg-[var(--bg-flat-50)]'}`}
          onClick={() => setActiveFinanceTab('expenses')}
        >
          <div className="flex items-center gap-2"><TrendingDown size={18} /> قائمة المصاريف</div>
        </button>
        <button
          className={`px-5 py-2.5 text-sm font-bold rounded-xl transition-all ${activeFinanceTab === 'treasury' ? 'bg-[#2980B9] text-white shadow-md' : 'text-[var(--text-muted)] hover:bg-[var(--bg-flat-50)]'}`}
          onClick={() => setActiveFinanceTab('treasury')}
        >
          <div className="flex items-center gap-2"><Wallet size={18} /> خزينة الشركة</div>
        </button>
        <button
          className={`px-5 py-2.5 text-sm font-bold rounded-xl transition-all ${activeFinanceTab === 'debts' ? 'bg-[#F39C12] text-white shadow-md' : 'text-[var(--text-muted)] hover:bg-[var(--bg-flat-50)]'}`}
          onClick={() => setActiveFinanceTab('debts')}
        >
          <div className="flex items-center gap-2"><HandCoins size={18} /> الديون والسلف</div>
        </button>
      </div>

      {activeFinanceTab === 'treasury' && <TreasuryPage defaultTab="treasury" />}
      {activeFinanceTab === 'debts' && <TreasuryPage defaultTab="debts" />}

      {activeFinanceTab === 'expenses' && (
        <>
          {/* ── Summary cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="الإجمالي" iqd={grandTotal} color="#E74C3C" icon={<TrendingDown />} />
        {byCategory.map(c => (
          <StatCard key={c.key} label={c.label} iqd={c.total} color={c.color} />
        ))}
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <FilterBtn active={catFilter === ''} color="#E74C3C" onClick={() => setCatFilter('')}>الكل</FilterBtn>
        {categories.map(c => (
          <FilterBtn key={c.key} active={catFilter === c.key} color={c.color} onClick={() => setCatFilter(c.key)}>{c.label}</FilterBtn>
        ))}
        <div style={{ marginRight: 'auto' }}>
          <DateRangeFilter from={fromDate} to={toDate} onFrom={setFromDate} onTo={setToDate} onClear={() => { setFromDate(''); setToDate(''); }} />
        </div>
      </div>

      {/* ── Add Form ── */}
      {showForm && (
        <form onSubmit={submit} className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ ...S.cardPad, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
            <LabeledInput label="التصنيف" required>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={S.input}>
                <option value="">— اختر التصنيف —</option>
                {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </LabeledInput>
            <LabeledInput label="التصنيف الفرعي">
              <input value={form.subcategory} onChange={e => setForm(f => ({ ...f, subcategory: e.target.value }))} style={S.input} placeholder="اختياري" />
            </LabeledInput>
            {form.category === 'salary' && (
              <LabeledInput label="الموظف" required>
                <select value={form.employee_id} onChange={e => {
                  const empId = e.target.value;
                  const emp = employees.find(x => x.id == empId);
                  const base = emp ? (emp.base_salary_iqd || 0) : 0;
                  setForm(f => ({
                    ...f,
                    employee_id: empId,
                    base_salary: base,
                    amount_iqd: base,
                    bonus: '',
                    deduction: ''
                  }));
                }} style={S.input} required>
                  <option value="">— اختر الموظف —</option>
                  {employees.filter(emp => emp.active).map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </LabeledInput>
            )}
            {form.category === 'salary' && (
              <>
                <LabeledInput label="مكافأة (د.ع)">
                  <input type="text" inputMode="decimal" min="0" value={form.bonus} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => {
                    const b = parseInt(e.target.value) || '';
                    const d = parseInt(form.deduction) || 0;
                    const base = parseInt(form.base_salary) || 0;
                    setForm(f => ({ ...f, bonus: e.target.value, amount_iqd: base + (b || 0) - d }));
                  }} style={S.input} placeholder="0" />
                </LabeledInput>
                <LabeledInput label="استقطاع (د.ع)">
                  <input type="text" inputMode="decimal" min="0" value={form.deduction} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => {
                    const d = parseInt(e.target.value) || '';
                    const b = parseInt(form.bonus) || 0;
                    const base = parseInt(form.base_salary) || 0;
                    setForm(f => ({ ...f, deduction: e.target.value, amount_iqd: base + b - (d || 0) }));
                  }} style={S.input} placeholder="0" />
                </LabeledInput>
              </>
            )}
            <LabeledInput label={form.category === 'salary' ? "الصافي (IQD)" : "المبلغ (IQD)"} required hint={expPreviewUSD > 0 ? `≈ ${formatUSD(expPreviewUSD)}` : undefined}>
              <input type="text" inputMode="decimal" min="1" value={form.amount_iqd}
                onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                onChange={e => setForm(f => ({ ...f, amount_iqd: e.target.value }))}
                style={S.input} required placeholder="250000" />
            </LabeledInput>
            <LabeledInput label="التاريخ">
              <input type="date" value={form.expense_date}
                onChange={e => setForm(f => ({ ...f, expense_date: e.target.value }))}
                style={S.input} />
            </LabeledInput>
            <LabeledInput label="البيان" style={{ gridColumn: '1 / -1' }}>
              <input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} style={S.input} placeholder="وصف المصروف..." />
            </LabeledInput>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" style={btn('#E74C3C')}>{editId ? 'حفظ التعديلات' : 'إضافة مصروف'}</button>
            <button type="button" onClick={() => { setShowForm(false); setForm(BLANK_EXP()); setEditId(null); }} style={btn('#F1F5F9', 'var(--text-gray, #64748B)')}>إلغاء</button>
          </div>
        </form>
      )}

      {/* ── Table ── */}
      <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={S.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
                {['التاريخ', 'التصنيف', 'التصنيف الفرعي', 'المبلغ', 'البيان', ''].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {expenses.map((exp, i) => {
                const cat = catMap[exp.category];
                return (
                  <tr key={exp.id} style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)', background: i % 2 ? 'var(--bg-flat-50, #FAFAFA)' : 'var(--bg-flat-white, white)' }}>
                    <td style={td({ color: 'var(--text-gray, #64748B)' })}>{exp.expense_date}</td>
                    <td style={td()}>
                      <span style={pill((cat?.color || 'var(--text-gray, #64748B)') + '22', cat?.color || 'var(--text-gray, #64748B)')}>
                        {cat?.label || exp.category}
                        {exp.category === 'salary' && exp.employee_name ? ` (${exp.employee_name})` : ''}
                      </span>
                    </td>
                    <td style={td({ color: 'var(--text-muted, #94A3B8)' })}>{exp.subcategory || '—'}</td>
                    <td style={td()}>
                      <Dual iqd={exp.amount_iqd} usd={exp.amount_usd} bold redIqd />
                    </td>
                    <td style={td({ color: 'var(--text-gray, #64748B)', maxWidth: 200 })}>{exp.note || '—'}</td>
                    <td style={td()}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button type="button" onClick={() => openEdit(exp)} title="تعديل" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2980B9', padding: '4px 6px', borderRadius: 6, display: 'flex' }}>
                          <Edit3 size={15} />
                        </button>
                        <button type="button" onClick={() => handleDelete(exp.id)} title="حذف" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E74C3C', padding: '4px 6px', borderRadius: 6, display: 'flex' }}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {expenses.length === 0 && <EmptyRow cols={6} msg="لا توجد مصاريف" />}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Mini pie if data ── */}
      {pieData.length > 0 && (
        <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ ...S.cardPad, marginTop: 16 }}>
          <h3 style={S.sectionTitle}>توزيع المصاريف</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'center' }}>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={3} dataKey="value" stroke="none">
                  {pieData.map((_, idx) => (
                    <Cell key={idx} fill={byCategory[idx]?.color || 'var(--text-muted, #94A3B8)'} />
                  ))}
                </Pie>
                <Tooltip formatter={v => [formatIQD(v), '']} contentStyle={{ backgroundColor: 'var(--bg-flat-white, white)', border: '1px solid var(--border-light, #E2E8F0)', color: 'var(--text-dark-h, #0F1E33)', borderRadius: 12, fontFamily: 'Tajawal, sans-serif', fontSize: 12 }} itemStyle={{ color: 'var(--text-dark-s, #374151)' }} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {byCategory.map(c => (
                <div key={c.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.color, display: 'inline-block' }} />
                    <span className="dark:!text-gray-100" style={{ fontSize: 13, color: 'var(--text-dark-s, #374151)' }}>{c.label}</span>
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#E74C3C' }}>{formatIQD(c.total)}</div>
                    <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)' }}>{formatUSD(iqd2usd(c.total))}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
